import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { surveyMessages } from './i18n';
import { configurationContext } from '../../../contexts/configuration';
import { genericMessages } from '../../../generic.i18n';
import { frameStateContext } from '../../../contexts/frame/context';
import { ReconnectChat } from '../../reconnectChat/ReconnectChat';
import { useIntl } from 'react-intl';
import {
	IChatMessagesOut,
	IChatEventMessage,
	MessageType,
	EventType,
	IChatEventFormMessage,
} from '@enuan/chat__utils';
import { Form } from '../../form';
import { useSendMessage } from '../../../hooks';
import { useSelector, useDispatch } from 'react-redux';
import {
	getLastCommandShowSurveyMessage,
	surveySlice,
} from '../../../reduxStore';
import { getFormLabels } from '../../../utils/getFormLabels';
import { useLogError } from '../../ErrorBoundary/useLogError';
import { configContext } from '../../../contexts/config/context';
import { FormFrame } from '../FormFrame';
import { styled } from '@mui/material/styles';

export const SurveyScreen = styled(
	({ className }: { className?: string }) => {
		const { survey, chat } = useContext(configurationContext);
		const { survey: surveyEnabled } = useContext(configContext);

		const logError = useLogError('SurveyScreen', { survey });

		const { togglePage: setPage } = useContext(frameStateContext);

		const intl = useIntl();
		const header_description = intl.formatMessage(
			surveyMessages.header_description,
		);
		const header_title = intl.formatMessage(surveyMessages.header_title);
		const invalid_configuration = intl.formatMessage(
			surveyMessages.invalid_configuration,
		);
		const summary_title = intl.formatMessage(surveyMessages.summary_title);
		const message = intl.formatMessage(genericMessages.message, { count: 2 });
		const go_back_to = intl.formatMessage(genericMessages.go_back_to, {
			page: message,
		});

		const { metadata: { id = '' } = {} } =
			useSelector(getLastCommandShowSurveyMessage) ?? {};

		const dispatch = useDispatch();
		const { toggleChat, togglePage } = useContext(frameStateContext);

		const onSurveyDone = useCallback(() => {
			// TODO handle killChat
			toggleChat(false);
			togglePage('messages');
			dispatch(surveySlice.actions.reset());
		}, [toggleChat, togglePage, dispatch]);

		const [readOnly, setReadOnly] = useState(false);
		const sendMessage = useSendMessage();

		useEffect(() => {
			const event: IChatMessagesOut<IChatEventMessage> = {
				_type: MessageType.EVENT,
				_data: { name: EventType.SURVEY_SHOWN },
			};
			sendMessage(event, true).catch((error) =>
				logError('surveyShown', { error, payload: { event } }),
			);
		}, [sendMessage, logError]);

		const header = useMemo(
			() => ({
				...survey?.header,
				title: survey?.header?.title ?? header_title,
				description: survey?.header?.description ?? header_description,
				logo: survey?.header?.logo ?? chat?.logo,
				summary_title: survey?.header?.summary_title ?? summary_title,
			}),
			[
				survey?.header,
				header_title,
				header_description,
				summary_title,
				chat?.logo,
			],
		);

		const onSubmitAction = useCallback(
			(values: Record<string, any>) => {
				const formLabels = getFormLabels(survey?.items, values);

				const event: IChatMessagesOut<IChatEventFormMessage> = {
					_type: MessageType.EVENT,
					_data: {
						name: EventType.SURVEY_DATA,
						parameters: {
							data: values,
							metadata: {
								command_id: id,
								labels: {
									form: formLabels,
									header,
								},
							},
						},
					},
				};

				setReadOnly(true);
				sendMessage(event)
					.then(() => onSurveyDone())
					.catch((error) => {
						setReadOnly(false);
						logError('submit', { error, payload: { event } });
					});
				return true;
			},
			[survey?.items, header, id, onSurveyDone, sendMessage, logError],
		);

		return (
			<FormFrame
				className={className}
				header={header}
				wave="right"
				body={
					<>
						{survey && surveyEnabled && survey.items ? (
							<Form
								prefix={'survey'}
								onSubmitAction={onSubmitAction}
								messages={surveyMessages}
								readOnly={readOnly}
								formConfig={survey}
							/>
						) : (
							<>
								<button onClick={() => setPage('messages')}>
									{go_back_to}
								</button>
								<h5>{invalid_configuration}</h5>
							</>
						)}
					</>
				}
				footer={<ReconnectChat />}
			/>
		);
	},
	{ target: 'SurveyScreen-root', label: 'SurveyScreen' },
)({});
